import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import AuthLayout from '../../components/layout/AuthLayout';
import { Mail, AlertCircle, CheckCircle } from 'lucide-react';

// VALUTTEN Logo component
const ValuttenLogo = () => (
  <img
    src="/valutten-logo.jpg"
    alt="VALUTTEN"
    className="h-12 w-auto object-contain"
  />
);

const PasswordResetPage = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      // Call sendPasswordReset Cloud Function
      const sendPasswordReset = httpsCallable(functions, 'sendPasswordReset');
      const result = await sendPasswordReset({ email });

      if ((result.data as any).success) {
        setMessage('Password reset email sent! Check your inbox for further instructions.');
      } else {
        setError((result.data as any).message || 'Failed to send reset email. Please try again.');
      }
    } catch (err: any) {
      console.error('Password reset error:', err);

      // Handle rate limiting
      if (err.code === 'functions/resource-exhausted' || err.message.includes('rate limit')) {
        setError('Too many reset attempts. Please wait a few minutes and try again.');
      } else if (err.code === 'functions/unauthenticated') {
        setError('Email address not found. Please check and try again.');
      } else {
        setError('Failed to send reset email. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <AuthLayout>
      <div className="w-full max-w-lg p-8 rounded-xl bg-slate-900/80 border border-slate-800">
        {/* VALUTTEN Header */}
        <div className="flex flex-col items-center justify-center mb-6">
          <ValuttenLogo />
          <div className="mt-4 w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <Mail className="w-7 h-7 text-emerald-500" />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-center mb-2 text-white">Reset Password</h1>
        <p className="text-center text-slate-400 text-sm mb-6">
          Enter your email address and we'll send you a link to reset your password.
        </p>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Success Message */}
        {message && (
          <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <p className="text-green-400 text-sm">{message}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
              Email address
            </label>
            <input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e: any) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              required
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-700 disabled:text-slate-500 text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-slate-900"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Sending...
              </span>
            ) : (
              'Send Reset Link'
            )}
          </button>
        </form>

        <p className="text-center mt-6 text-slate-400 text-sm">
          Remember your password?{' '}
          <Link to="/login" className="text-emerald-400 hover:text-emerald-300 font-medium">
            Back to Login
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
};

export default PasswordResetPage;